import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { CompanyTodoCardProps, CompanyTodoGroupCount } from "models/propTypes/allCompaniesPropTypes";
import { AllCompaniesConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { ButtonBase, Grid, Skeleton } from "@mui/material";
import { Icon, SimpleTooltip, Tags } from "components/common";
import CompanyTodoCountBadge from "./companyTodoCountBadge";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const getGroupCountText = (groupCount: CompanyTodoGroupCount) =>
  LocalizationHelperInstance.getFormattedMessage(
    `Todos.AllCompaniesGroupCount_${WintSuperkollAPIModel.TodoGroup[groupCount.group]}`,
    undefined,
    { count: groupCount.count }
  );

/** One company in the overview. The whole card opens the company; the chevron is a plain indicator, not a second control. */
const CompanyCard: React.FC<CompanyTodoCardProps> = (props) => {
  const { card, onClick } = props;

  const loading = card.state === "loading";
  const loaded = card.state === "loaded";
  const done = loaded && card.total === 0;

  /** The largest groups spelled out, the rest behind a "+N" with the full list on hover. */
  const getGroupsLine = () => {
    const shown = card.groupCounts.slice(0, AllCompaniesConstants.SummaryGroupLimit);
    const hidden = card.groupCounts.slice(AllCompaniesConstants.SummaryGroupLimit);

    return (
      <div className={cssExports.companyTodoCardGroups}>
        {shown.map((groupCount, index) => (
          <React.Fragment key={groupCount.group}>
            {index > 0 ? ", " : ""}
            <span className={cssExports.companyTodoCardGroup}>{getGroupCountText(groupCount)}</span>
          </React.Fragment>
        ))}
        {hidden.length > 0 ? (
          <SimpleTooltip text={hidden.map(getGroupCountText).join(", ")}>
            <span className={cssExports.companyTodoCardMoreGroups}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.AllCompaniesMoreGroups, undefined, {
                count: hidden.length,
              })}
            </span>
          </SimpleTooltip>
        ) : (
          <></>
        )}
      </div>
    );
  };

  const getSummary = () => {
    if (loading)
      return (
        <>
          <Skeleton variant="rounded" width="50%" height={8} />
          <Skeleton variant="rounded" width="33%" height={8} />
        </>
      );
    if (!loaded) return <></>;

    return (
      <>
        {card.overdue > 0 ? (
          <Tags
            tags={[
              LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.AllCompaniesOverdueCount, undefined, {
                count: card.overdue,
              }),
            ]}
            className={cssExports.redTag}
          />
        ) : (
          <></>
        )}
        {done ? (
          <Tags
            tags={[LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.AllCompaniesAllDone)]}
            className={cssExports.greenTag}
          />
        ) : (
          getGroupsLine()
        )}
      </>
    );
  };

  return (
    <ButtonBase
      className={cssExports.companyTodoCard}
      onClick={() => onClick(card)}
      data-testid={`company-todo-card-${card.companyId}`}
    >
      <Grid container wrap="nowrap" alignItems="flex-start" className={cssExports.companyTodoCardTop}>
        <Grid item xs className={cssExports.companyTodoCardTitle}>
          {card.companyName}
        </Grid>
        <Grid item>
          {loading ? (
            <Skeleton variant="circular" width={32} height={32} />
          ) : loaded ? (
            <CompanyTodoCountBadge count={card.total} overdue={card.overdue > 0} />
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
      <Grid container wrap="nowrap" alignItems="flex-end" className={cssExports.companyTodoCardFooter}>
        <Grid item xs className={cssExports.companyTodoCardSummary}>
          {getSummary()}
        </Grid>
        <Grid item>
          <Icon icon={IconDefinitionConstants.WChevronRight} className={cssExports.companyTodoCardChevron} />
        </Grid>
      </Grid>
    </ButtonBase>
  );
};

export default CompanyCard;
